/**
 * InventoryMovementsView — תנועות מלאי
 *
 * Filterable movement log: one row per document line that affected inventory.
 * Filters: date range, warehouse, product search, supplier, effect direction,
 *          document type.
 * Clicking a row deep-links to DocumentGenerationPage journal tab.
 * Summary chips show total in/out quantities AND monetary totals.
 * Excel export of filtered results.
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircleArrowDown as ArrowDownCircle,
  CircleArrowUp as ArrowUpCircle,
  ArrowRightLeft,
  CalendarDays,
  Search,
  Warehouse,
  Truck,
  Loader as Loader2,
  CircleAlert as AlertCircle,
  PackageOpen,
  RefreshCw,
  ExternalLink,
  ListFilter as Filter,
  FileSpreadsheet,
  FileText,
  FileDown,
  Wrench,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTableSort, sortRows } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/ui/SortableTh';

// ─── Types ────────────────────────────────────────────────────────────────────

type EffectFilter = 'all' | 'in' | 'out' | 'transfer' | 'adjustment';

interface MovementRow {
  movement_id:          string;
  document_id:          string;
  document_number:      number;
  document_type_id:     string;
  document_type_name:   string;
  document_type_code:   string;
  inventory_effect:     string;
  issue_date:           string;
  product_id:           string;
  sku:                  string | null;
  product_name:         string;
  image_url:            string | null;
  warehouse_id:         string | null;
  warehouse_name:       string | null;
  destination_wh_id:    string | null;
  destination_wh_name:  string | null;
  quantity:             number;
  delta:                number;
  unit_price:           number;
  line_total:           number;
  supplier_name:        string | null;
  client_name:          string | null;
}

interface WarehouseOption { id: string; name: string; }
interface SupplierOption  { id: string; name: string; }
interface DocTypeOption   { id: string; name: string; prefix: string; }

interface Props { companyId: string; }

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayISO(): string { return new Date().toISOString().slice(0, 10); }
function thirtyDaysAgo(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function formatQty(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return parseFloat(n.toFixed(3)).toString();
}

function formatCurrency(n: number): string {
  return n.toLocaleString('he-IL', {
    style: 'currency', currency: 'ILS',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function formatDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('he-IL');
}

// ─── Effect badge ─────────────────────────────────────────────────────────────

function EffectBadge({ effect }: { effect: string }) {
  if (effect === 'in') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                     text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
      <ArrowDownCircle className="w-3 h-3" />
      כניסה
    </span>
  );
  if (effect === 'out') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                     text-xs font-semibold bg-red-50 text-red-700 border border-red-100">
      <ArrowUpCircle className="w-3 h-3" />
      יציאה
    </span>
  );
  if (effect === 'transfer') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                     text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
      <ArrowRightLeft className="w-3 h-3" />
      העברה
    </span>
  );
  if (effect === 'adjustment') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                     text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
      <Wrench className="w-3 h-3" />
      התאמה
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                     text-xs font-semibold bg-gray-100 text-gray-500">
      —
    </span>
  );
}

// ─── Excel export ─────────────────────────────────────────────────────────────

async function exportToExcel(rows: MovementRow[], fromDate: string, toDate: string) {
  const XLSX = await import('xlsx');
  const wsData = rows.map(r => ({
    'תאריך': formatDate(r.issue_date),
    'סוג מסמך': r.document_type_name,
    'קוד': r.document_type_code,
    'מספר מסמך': r.document_number,
    'תנועה': r.inventory_effect === 'in' ? 'כניסה' :
             r.inventory_effect === 'out' ? 'יציאה' :
             r.inventory_effect === 'transfer' ? 'העברה' :
             r.inventory_effect === 'adjustment' ? 'התאמה' : r.inventory_effect,
    'מוצר': r.product_name,
    'מק"ט': r.sku ?? '',
    'ממחסן': r.inventory_effect === 'out' || r.inventory_effect === 'transfer'
              ? (r.warehouse_name ?? '') : '',
    'למחסן': r.inventory_effect === 'in' || r.inventory_effect === 'adjustment'
              ? (r.warehouse_name ?? '')
              : r.inventory_effect === 'transfer'
                ? (r.destination_wh_name ?? '') : '',
    'כמות': r.quantity,
    'שינוי': r.delta,
    'מחיר יחידה': r.unit_price,
    'סה"כ שורה': r.line_total,
    'ספק': r.supplier_name ?? '',
    'לקוח': r.client_name ?? '',
  }));

  const ws = XLSX.utils.json_to_sheet(wsData);
  ws['!cols'] = [
    { wch: 12 }, { wch: 22 }, { wch: 8 }, { wch: 10 }, { wch: 8 },
    { wch: 25 }, { wch: 14 }, { wch: 16 }, { wch: 16 },
    { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 18 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'תנועות מלאי');
  XLSX.writeFile(wb, `תנועות_מלאי_${fromDate}_${toDate}.xlsx`);
}

// ─── PDF export ──────────────────────────────────────────────────────────────

async function exportMovementsToPDF(tableRef: HTMLElement, fromDate: string, toDate: string) {
  const { exportTableToPDF } = await import('../../lib/pdfExport');
  await exportTableToPDF(tableRef, `תנועות_מלאי_${fromDate}_${toDate}.pdf`, { orientation: 'landscape' });
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InventoryMovementsView({ companyId }: Props) {
  const navigate = useNavigate();

  // ── Filter state ────────────────────────────────────────────────────────────
  const [fromDate,        setFromDate]        = useState(thirtyDaysAgo());
  const [toDate,          setToDate]          = useState(todayISO());
  const [search,          setSearch]          = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [supplierFilter,  setSupplierFilter]  = useState('all');
  const [effectFilter,    setEffectFilter]    = useState<EffectFilter>('all');
  const [docTypeFilter,   setDocTypeFilter]   = useState('all');

  // ── Data state ───────────────────────────────────────────────────────────────
  const [rows,       setRows]       = useState<MovementRow[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [suppliers,  setSuppliers]  = useState<SupplierOption[]>([]);
  const [docTypes,   setDocTypes]   = useState<DocTypeOption[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [exporting,  setExporting]  = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  const tableRef = useRef<HTMLDivElement>(null);

  // ── Load dropdowns once ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      supabase.from('inventory_warehouses').select('id, name').eq('company_id', companyId).eq('is_external', false).order('name'),
      supabase.from('inventory_suppliers').select('id, name').eq('company_id', companyId).order('name'),
      supabase.from('document_types').select('id, name, prefix').eq('company_id', companyId).order('name'),
    ]).then(([wRes, sRes, dtRes]) => {
      if (wRes.data) setWarehouses(wRes.data as WarehouseOption[]);
      if (sRes.data) setSuppliers(sRes.data as SupplierOption[]);
      if (dtRes.data) {
        const seen = new Set<string>();
        const unique: DocTypeOption[] = [];
        for (const dt of dtRes.data as DocTypeOption[]) {
          const key = `${dt.name}|${dt.prefix}`;
          if (!seen.has(key)) { seen.add(key); unique.push(dt); }
        }
        setDocTypes(unique);
      }
    });
  }, [companyId]);

  // ── Fetch movements ──────────────────────────────────────────────────────────
  const fetchMovements = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);

    const { data, error: rpcErr } = await supabase.rpc('get_inventory_movements', {
      p_company_id:       companyId,
      p_from_date:        fromDate || null,
      p_to_date:          toDate   || null,
      p_warehouse_id:     warehouseFilter !== 'all' ? warehouseFilter : null,
      p_supplier_id:      supplierFilter  !== 'all' ? supplierFilter  : null,
      p_effect_filter:    effectFilter    !== 'all' ? effectFilter    : null,
      p_document_type_id: docTypeFilter   !== 'all' ? docTypeFilter   : null,
      p_product_id:       null,
    }).limit(10000);

    setLoading(false);
    if (rpcErr) {
      setError(`שגיאה: ${rpcErr.message}`);
      setRows([]);
      return;
    }
    setRows((data as MovementRow[]) ?? []);
  }, [companyId, fromDate, toDate, warehouseFilter, supplierFilter, effectFilter, docTypeFilter]);

  useEffect(() => { fetchMovements(); }, [fetchMovements]);

  const { sort, toggle } = useTableSort<string>('issue_date', 'desc');

  // ── Local search filter ──────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? rows.filter(r =>
          r.product_name.toLowerCase().includes(q) ||
          (r.sku ?? '').toLowerCase().includes(q) ||
          (r.document_type_name ?? '').toLowerCase().includes(q) ||
          (r.document_type_code ?? '').toLowerCase().includes(q) ||
          String(r.document_number).includes(q)
        )
      : rows;
    return sortRows(base, sort.key, sort.dir);
  }, [rows, search, sort]);

  // ── Aggregates ───────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    const inRows  = filtered.filter(r => r.inventory_effect === 'in');
    const outRows = filtered.filter(r => r.inventory_effect === 'out');
    const adjRows = filtered.filter(r => r.inventory_effect === 'adjustment');
    return {
      inQty:       inRows.reduce((s, r) => s + r.quantity, 0),
      inAmount:    inRows.reduce((s, r) => s + (r.line_total ?? 0), 0),
      outQty:      outRows.reduce((s, r) => s + r.quantity, 0),
      outAmount:   outRows.reduce((s, r) => s + (r.line_total ?? 0), 0),
      transfers:   filtered.filter(r => r.inventory_effect === 'transfer').length,
      adjQty:      adjRows.reduce((s, r) => s + r.quantity, 0),
    };
  }, [filtered]);

  // ── Active doc-type label for summary ────────────────────────────────────────
  const activeDocTypeName = useMemo(() => {
    if (docTypeFilter === 'all') return null;
    return docTypes.find(dt => dt.id === docTypeFilter)?.name ?? null;
  }, [docTypeFilter, docTypes]);

  // ── Deep-link to document journal ────────────────────────────────────────────
  const openDocument = (docId: string) => {
    navigate(`/app/inventory/documents/new?tab=journal&highlight=${docId}`);
  };

  // ── Excel export handler ─────────────────────────────────────────────────────
  const handleExport = async () => {
    if (filtered.length === 0) return;
    setExporting(true);
    try {
      await exportToExcel(filtered, fromDate, toDate);
    } catch {
      setError('שגיאה בייצוא לאקסל');
    } finally {
      setExporting(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-5" dir="rtl">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-xl">
            <ArrowRightLeft className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">תנועות מלאי</h2>
            <p className="text-xs text-gray-500">כל תנועות הכניסה, יציאה, העברות והתאמות לפי טווח תאריכים</p>
          </div>
        </div>

        {/* Export buttons */}
        {filtered.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200
                         bg-emerald-50 text-sm text-emerald-700 hover:bg-emerald-100
                         disabled:opacity-50 transition-colors"
            >
              <FileSpreadsheet className={`w-4 h-4 ${exporting ? 'animate-spin' : ''}`} />
              ייצוא לאקסל
            </button>
            <button
              onClick={async () => {
                if (!tableRef.current) return;
                setPdfExporting(true);
                try {
                  await exportMovementsToPDF(tableRef.current, fromDate, toDate);
                } catch (e) {
                  console.error(e); alert('שגיאה ביצירת PDF');
                } finally {
                  setPdfExporting(false);
                }
              }}
              disabled={pdfExporting || filtered.length === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-200
                         bg-blue-50 text-sm text-blue-700 hover:bg-blue-100
                         disabled:opacity-50 transition-colors"
            >
              <FileDown className={`w-4 h-4 ${pdfExporting ? 'animate-spin' : ''}`} />
              PDF
            </button>
          </div>
        )}
      </div>

      {/* Filter toolbar */}
      <div className="flex flex-wrap gap-3 items-end">

        {/* From date */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <CalendarDays className="w-3 h-3" />
            מתאריך
          </label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
        </div>

        {/* To date */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <CalendarDays className="w-3 h-3" />
            עד תאריך
          </label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
        </div>

        {/* Effect filter */}
        <div className="flex flex-col gap-1 min-w-[140px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <Filter className="w-3 h-3" />
            סוג תנועה
          </label>
          <select value={effectFilter} onChange={e => setEffectFilter(e.target.value as EffectFilter)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white
                       focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent">
            <option value="all">הכל</option>
            <option value="in">כניסה</option>
            <option value="out">יציאה</option>
            <option value="transfer">העברה</option>
            <option value="adjustment">התאמה</option>
          </select>
        </div>

        {/* Document type filter */}
        <div className="flex flex-col gap-1 min-w-[180px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            סוג מסמך
          </label>
          <select value={docTypeFilter} onChange={e => setDocTypeFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white
                       focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent">
            <option value="all">כל סוגי המסמכים</option>
            {docTypes.map(dt => (
              <option key={dt.id} value={dt.id}>{dt.name} ({dt.prefix})</option>
            ))}
          </select>
        </div>

        {/* Warehouse filter */}
        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <Warehouse className="w-3 h-3" />
            מחסן
          </label>
          <select value={warehouseFilter} onChange={e => setWarehouseFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white
                       focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent">
            <option value="all">כל המחסנים</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>

        {/* Supplier filter */}
        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <Truck className="w-3 h-3" />
            ספק
          </label>
          <select value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white
                       focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent">
            <option value="all">כל הספקים</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        {/* Search */}
        <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <Search className="w-3 h-3" />
            חיפוש
          </label>
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4
                               text-gray-400 pointer-events-none" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="מוצר, מקט, סוג מסמך, מספר…"
              className="w-full pr-9 pl-3 py-2 border border-gray-200 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
          </div>
        </div>

        {/* Refresh */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 invisible">רענן</label>
          <button onClick={fetchMovements} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200
                       text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            רענן
          </button>
        </div>
      </div>

      {/* Summary chips */}
      {!loading && filtered.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {/* Total in */}
          <div className="inline-flex flex-col px-3 py-1.5 rounded-xl text-xs font-medium
                          bg-emerald-50 text-emerald-700 border border-emerald-100">
            <div className="flex items-center gap-1.5">
              <ArrowDownCircle className="w-3 h-3" />
              {activeDocTypeName ? `כניסות (${activeDocTypeName})` : 'כניסות'}
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <span>{formatQty(totals.inQty)} יח'</span>
              <span className="font-bold">{formatCurrency(totals.inAmount)}</span>
            </div>
          </div>

          {/* Total out */}
          <div className="inline-flex flex-col px-3 py-1.5 rounded-xl text-xs font-medium
                          bg-red-50 text-red-700 border border-red-100">
            <div className="flex items-center gap-1.5">
              <ArrowUpCircle className="w-3 h-3" />
              {activeDocTypeName ? `יציאות (${activeDocTypeName})` : 'יציאות'}
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <span>{formatQty(totals.outQty)} יח'</span>
              <span className="font-bold">{formatCurrency(totals.outAmount)}</span>
            </div>
          </div>

          {/* Transfers */}
          {totals.transfers > 0 && (
            <div className="inline-flex flex-col px-3 py-1.5 rounded-xl text-xs font-medium
                            bg-blue-50 text-blue-700 border border-blue-100">
              <div className="flex items-center gap-1.5">
                <ArrowRightLeft className="w-3 h-3" />
                העברות
              </div>
              <span className="mt-0.5">{totals.transfers} שורות</span>
            </div>
          )}

          {/* Adjustments */}
          {totals.adjQty > 0 && (
            <div className="inline-flex flex-col px-3 py-1.5 rounded-xl text-xs font-medium
                            bg-amber-50 text-amber-700 border border-amber-100">
              <div className="flex items-center gap-1.5">
                <Wrench className="w-3 h-3" />
                התאמות
              </div>
              <span className="mt-0.5">{formatQty(totals.adjQty)} יח'</span>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200
                        rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Table card */}
      <div ref={tableRef} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        {loading && (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">טוען תנועות…</span>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
            <PackageOpen className="w-10 h-10 opacity-40" />
            <p className="text-sm font-medium">
              {rows.length === 0 ? 'אין תנועות מלאי בטווח התאריכים הנבחר' : 'אין תוצאות התואמות את הסינון'}
            </p>
            <p className="text-xs text-gray-400">נסה לשנות את טווח התאריכים או הסינונים</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-gray-500 text-xs font-medium">
                  <th className="text-right px-4 py-3 w-10">תמונה</th>
                  <SortableTh label="מוצר" sortKey="product_name" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="תנועה" sortKey="inventory_effect" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="ממחסן" sortKey="warehouse_name" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="למחסן" sortKey="destination_wh_name" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="מסמך" sortKey="document_type_name" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="תאריך" sortKey="issue_date" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="כמות" sortKey="quantity" activeKey={sort.key} dir={sort.dir} onSort={toggle} align="left" />
                  <SortableTh label="סה״כ שורה" sortKey="line_total" activeKey={sort.key} dir={sort.dir} onSort={toggle} align="left" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, idx) => (
                  <tr key={row.movement_id}
                    onClick={() => openDocument(row.document_id)}
                    className={`border-b border-gray-50 hover:bg-orange-50/40 transition-colors cursor-pointer
                      ${idx % 2 === 0 ? '' : 'bg-gray-50/20'}`}>

                    {/* Image */}
                    <td className="px-4 py-3">
                      {row.image_url ? (
                        <img src={row.image_url} alt={row.product_name}
                          className="w-8 h-8 rounded-lg object-cover border border-gray-100" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                          <PackageOpen className="w-4 h-4 text-gray-300" />
                        </div>
                      )}
                    </td>

                    {/* Product */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800 text-sm">{row.product_name}</div>
                      {row.sku && (
                        <div className="font-mono text-xs text-gray-400">{row.sku}</div>
                      )}
                    </td>

                    {/* Effect */}
                    <td className="px-4 py-3">
                      <EffectBadge effect={row.inventory_effect} />
                    </td>

                    {/* From warehouse */}
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {(row.inventory_effect === 'out' || row.inventory_effect === 'transfer')
                        ? (
                          <div className="flex items-center gap-1">
                            <Warehouse className="w-3 h-3 text-gray-400 shrink-0" />
                            {row.warehouse_name ?? '—'}
                          </div>
                        )
                        : <span className="text-gray-300">—</span>
                      }
                    </td>

                    {/* To warehouse */}
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {row.inventory_effect === 'transfer'
                        ? (
                          <div className="flex items-center gap-1">
                            <Warehouse className="w-3 h-3 text-blue-400 shrink-0" />
                            {row.destination_wh_name ?? '—'}
                          </div>
                        )
                        : (row.inventory_effect === 'in' || row.inventory_effect === 'adjustment')
                          ? (
                            <div className="flex items-center gap-1">
                              <Warehouse className="w-3 h-3 text-gray-400 shrink-0" />
                              {row.warehouse_name ?? '—'}
                            </div>
                          )
                          : <span className="text-gray-300">—</span>
                      }
                    </td>

                    {/* Document */}
                    <td className="px-4 py-3">
                      <div className="group flex items-center gap-1.5 text-xs text-gray-700">
                        <span className="font-medium">{row.document_type_code}</span>
                        <span className="text-gray-400">#{row.document_number}</span>
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100
                                                 transition-opacity text-orange-500" />
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{row.document_type_name}</div>
                      {(row.supplier_name || row.client_name) && (
                        <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                          <Truck className="w-3 h-3 shrink-0" />
                          {row.supplier_name ?? row.client_name}
                        </div>
                      )}
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {formatDate(row.issue_date)}
                    </td>

                    {/* Quantity */}
                    <td className="px-4 py-3 text-left">
                      <span className={`font-semibold text-sm
                        ${row.inventory_effect === 'in'         ? 'text-emerald-600' :
                          row.inventory_effect === 'out'        ? 'text-red-600' :
                          row.inventory_effect === 'transfer'   ? 'text-blue-600' :
                          row.inventory_effect === 'adjustment' ? 'text-amber-600' : 'text-gray-600'}`}>
                        {row.inventory_effect === 'in' || row.inventory_effect === 'adjustment' ? '+' :
                         row.inventory_effect === 'out' ? '-' : ''}
                        {formatQty(row.quantity)}
                      </span>
                    </td>

                    {/* Line total */}
                    <td className="px-4 py-3 text-left text-gray-700 text-sm">
                      {row.line_total ? formatCurrency(row.line_total) : <span className="text-gray-300">—</span>}
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/60
                          flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-500">
            <span>
              {filtered.length} תנועות
              {filtered.length !== rows.length && ` (מתוך ${rows.length})`}
            </span>
            <span className="flex-1" />
            <span>
              {fromDate && toDate && (
                <>{formatDate(fromDate)} — {formatDate(toDate)}</>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
